/**
 * ChainSkillEffect — Poison tree plague path active (Fork 2): manual epidemic
 * broadcast.
 *
 * Targets a currently poisoned enemy and immediately triggers a full-strength
 * spread to all non-poisoned enemies within 5 tiles. Uses
 * ChainTargetingSystem in 'all' mode with damageRetention 1.0 — no generation
 * degradation. Every target in range receives poison at full base intensity.
 *
 * Precondition: the targeted enemy must carry the `poison` status. If the
 * target is not poisoned, the skill fizzles without consuming the cooldown.
 *
 * On successful activation, notifies CooldownHookSystem with conditionId
 * 'chain_fired' so Outbreak's cooldown refund hook can fire.
 *
 * Upgrade keys via resolver:
 * - Outbreak (chain_upgrade_1): registers a CooldownHook externally —
 *   Chain only needs to fire the notify trigger.
 * - Mutation (chain_upgrade_2): modifies Contagion, not Chain directly.
 * - Vectors (tree passive): `jumpBonusDotChance` (max, tree scope) — 15% chance
 *   per hop to apply a bonus independent 2-second poison DoT at flat intensity 3
 *   (bypasses Toxicologist multiplier). `jumpBonusDotDurationMs` (max, tree scope).
 *
 * Pure logic — no Phaser deps.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see poison-spec.md § Chain
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type {
  ChainConfig,
  ChainTarget,
  ChainTargetResult,
} from '../../../systems/ChainTargetingSystem.js';
import type { CooldownTrigger } from '../../../systems/CooldownHookSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ChainDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getTreePassiveEffect(
      treeName: string,
      key: string,
      aggregation: 'sum' | 'product' | 'max' | 'min',
    ): number;
  };
  readonly statusEffects: {
    apply(defId: string, opts: {
      sourceSkillId: string;
      intensity?: number;
      durationMs?: number;
    }): void;
  };
  readonly chainTargeting: {
    findChainTargets(
      config: ChainConfig,
      candidates: Iterable<ChainTarget>,
    ): ChainTargetResult;
  };
  readonly target: {
    getCurrentTargetId(): string;
    isTargetPoisoned(): boolean;
  };
  readonly cooldownHooks: {
    notify(trigger: CooldownTrigger, baseCooldowns?: Map<string, number>): void;
  };
  readonly rng: {
    roll(): number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface ChainConfig_ {
  readonly cooldownMs: number;
  readonly baseRangeTiles: number;
  readonly baseIntensity: number;
}

const DEFAULT_CONFIG: ChainConfig_ = {
  cooldownMs: 2000,              // long cooldown tier
  baseRangeTiles: 5,
  baseIntensity: 3,              // poison status default intensity
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Flat intensity for Vectors bonus DoT — bypasses Toxicologist. */
const VECTORS_BONUS_INTENSITY = 3;

/** Record of a single Chain broadcast result. */
export interface ChainBroadcastResult {
  readonly originId: string;
  readonly targets: Array<{
    id: string;
    intensity: number;
    /** Whether the Vectors bonus DoT was applied on this hop. */
    bonusDotApplied?: boolean;
  }>;
  readonly totalSpread: number;
}

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ChainSkillEffect implements SkillEffect {
  readonly skillId = 'chain';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: ChainDeps;
  private readonly config: ChainConfig_;

  private _cooldownRemaining = 0;
  private _activationCount = 0;
  private _fizzleCount = 0;

  /** Last broadcast result for assertion queries. */
  private _lastBroadcastResult: ChainBroadcastResult | null = null;
  private _lastChainConfig: ChainConfig | null = null;

  constructor(deps: ChainDeps, config: Partial<ChainConfig_> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activationCount(): number { return this._activationCount; }
  get fizzleCount(): number { return this._fizzleCount; }
  get lastBroadcastResult(): ChainBroadcastResult | null { return this._lastBroadcastResult; }
  get lastChainConfig(): ChainConfig | null { return this._lastChainConfig; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Precondition: target must be poisoned
    if (!this.deps.target.isTargetPoisoned()) {
      this._fizzleCount++;
      // Fizzle — no cooldown consumed
      return false;
    }

    const targetId = this.deps.target.getCurrentTargetId();

    this._cooldownRemaining = this.config.cooldownMs;
    this._activationCount++;

    // Notify CooldownHookSystem for Outbreak's refund hook
    this.deps.cooldownHooks.notify(
      { type: 'onCondition', conditionId: 'chain_fired' },
    );

    // Store the origin ID; the actual spread happens in performChain()
    this._lastBroadcastResult = {
      originId: targetId,
      targets: [],
      totalSpread: 0,
    };

    return true;
  }

  /**
   * Perform the Chain broadcast from the targeted poisoned enemy outward.
   *
   * Call this after activate() with the origin enemy's position and all
   * nearby enemy candidates. Applies poison at full intensity to every
   * non-poisoned enemy within range.
   *
   * @param originX - X position of the targeted poisoned enemy (pixel coords)
   * @param originY - Y position of the targeted poisoned enemy
   * @param candidates - Nearby enemies eligible for infection
   * @returns The broadcast result
   */
  performChain(
    originX: number,
    originY: number,
    candidates: ChainTarget[],
  ): ChainBroadcastResult {
    const intensity = this.config.baseIntensity;
    const rangeTiles = this.config.baseRangeTiles;
    const originId = this._lastBroadcastResult?.originId ?? '';

    const chainConfig: ChainConfig = {
      originX,
      originY,
      maxTargets: candidates.length || Number.MAX_SAFE_INTEGER,
      maxRangeTiles: rangeTiles,
      damageRetention: 1.0,
      mode: 'all',
      sourceEntityId: originId,
      excludeStatus: 'poison',
    };

    this._lastChainConfig = chainConfig;

    const chainResult = this.deps.chainTargeting.findChainTargets(
      chainConfig,
      candidates,
    );

    const spreadTargets: ChainBroadcastResult['targets'] = [];

    for (const hit of chainResult.targets) {
      this.deps.statusEffects.apply('poison', {
        sourceSkillId: 'chain',
        intensity,
      });

      // Vectors bonus DoT: independent roll per hop
      const bonusDotApplied = this.rollVectorsBonusDot();

      spreadTargets.push({
        id: hit.target.id,
        intensity,
        bonusDotApplied,
      });
    }

    const result: ChainBroadcastResult = {
      originId,
      targets: spreadTargets,
      totalSpread: spreadTargets.length,
    };

    this._lastBroadcastResult = result;
    return result;
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
    this._lastBroadcastResult = null;
    this._lastChainConfig = null;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Roll for Vectors bonus DoT on a hop target. If the tree passive
   * `jumpBonusDotChance` is active and the roll succeeds, applies a
   * flat-intensity independent poison with sourceSkillId 'vectors_bonus'.
   * Intensity is VECTORS_BONUS_INTENSITY (3) — bypasses Toxicologist.
   *
   * @returns true if the bonus DoT was applied
   */
  private rollVectorsBonusDot(): boolean {
    const chance = this.deps.resolver.getTreePassiveEffect(
      'poison', 'jumpBonusDotChance', 'max',
    );
    if (chance <= 0) return false;

    if (this.deps.rng.roll() >= chance) return false;

    const durationMs = this.deps.resolver.getTreePassiveEffect(
      'poison', 'jumpBonusDotDurationMs', 'max',
    );

    this.deps.statusEffects.apply('poison', {
      sourceSkillId: 'vectors_bonus',
      intensity: VECTORS_BONUS_INTENSITY,
      durationMs: durationMs > 0 ? durationMs : 2000,
    });

    return true;
  }
}
